import React, { useState, useEffect } from 'react';
import {
  getCreditBalance,
  getPendingCreditBalance,
  getTransactionHistory,
  getCreditStats,
  withdrawCredits,
  Transaction
} from '../services/creditsService';
import { useLocation, useNavigate } from 'react-router-dom';
import PendingEarningsPanel from '../components/PendingEarningsPanel';
import DebugPanel from '../components/DebugPanel';
import { isAuthenticated } from '../services/authService';

// BTC price in USD (in a real app, this would come from an API)
const BTC_PRICE = 60000;

const AccountPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate('/login', { state: { from: location } });
      return;
    }

    setIsLoading(false);

    // Check if URL has tab parameter and set it as active
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location, navigate]);

  const renderTabContent = () => {
    switch(activeTab) {
      case 'profile':
        return <ProfileTab />;
      case 'settings':
        return <SettingsTab />;
      case 'payment':
        return <PaymentMethodsTab />;
      case 'credits':
        return <CreditsTab />;
      case 'notifications':
        return <NotificationsTab />;
      case 'debug':
        return <DebugPanel />;
      default:
        return <ProfileTab />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7F56D9]"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">My Account</h1>

        <div className="bg-[#1F1A27] rounded-lg overflow-hidden">
          <div className="flex overflow-x-auto border-b border-white/10">
            {['profile', 'settings', 'payment', 'credits', 'notifications', 'debug'].map((tab) => (
              <button
                key={tab}
                className={`px-6 py-4 text-base font-medium whitespace-nowrap ${
                  activeTab === tab
                    ? 'text-[#7F56D9] border-b-2 border-[#7F56D9]'
                    : 'text-[var(--color-muted)] hover:text-white'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileTab: React.FC = () => {
  return (
    <div>
      <h2 className="text-2xl font-medium mb-6">Profile Information</h2>

      <div className="flex flex-col md:flex-row gap-8 mb-8">
        <div className="flex-shrink-0">
          <div className="w-32 h-32 rounded-full bg-[#2A2633] flex items-center justify-center relative overflow-hidden">
            <img
              src="https://via.placeholder.com/128"
              alt="Profile"
              className="w-full h-full object-cover"
            />
            <button className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity text-white">
              Change
            </button>
          </div>
        </div>

        <div className="flex-grow space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                defaultValue="John"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                defaultValue="Doe"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                defaultValue="john.doe@example.com"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                defaultValue="johndoe"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>
          </div>

          <div>
            <label htmlFor="bio" className="block text-sm font-medium mb-2">
              Bio
            </label>
            <textarea
              id="bio"
              rows={4}
              defaultValue="Full-stack developer with 5 years of experience in React, Node.js, and TypeScript. Passionate about creating efficient and user-friendly web applications."
              className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
            ></textarea>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="text-xl font-medium mb-4">Social Profiles</h3>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#2A2633] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-[#6e87d8]">
                <path d="M12 2C6.477 2 2 6.477 2 12C2 16.991 5.657 21.128 10.438 21.879V14.89H7.898V12H10.438V9.797C10.438 7.291 11.93 5.907 14.215 5.907C15.309 5.907 16.453 6.102 16.453 6.102V8.562H15.193C13.95 8.562 13.563 9.333 13.563 10.124V12H16.336L15.893 14.89H13.563V21.879C18.343 21.129 22 16.99 22 12C22 6.477 17.523 2 12 2Z"/>
              </svg>
            </div>
            <div className="flex-grow">
              <input
                type="text"
                placeholder="Facebook profile URL"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#2A2633] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-[#2867B2]">
                <path d="M19 3A2 2 0 0 1 21 5V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V5A2 2 0 0 1 5 3H19M18.5 18.5V13.2A3.26 3.26 0 0 0 15.24 9.94C14.39 9.94 13.4 10.46 12.92 11.24V10.13H10.13V18.5H12.92V13.57C12.92 12.8 13.54 12.17 14.31 12.17A1.4 1.4 0 0 1 15.71 13.57V18.5H18.5M6.88 8.56A1.68 1.68 0 0 0 8.56 6.88C8.56 5.95 7.81 5.19 6.88 5.19A1.69 1.69 0 0 0 5.19 6.88C5.19 7.81 5.95 8.56 6.88 8.56M8.27 18.5V10.13H5.5V18.5H8.27Z"/>
              </svg>
            </div>
            <div className="flex-grow">
              <input
                type="text"
                placeholder="LinkedIn profile URL"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#2A2633] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-[#333333]">
                <path d="M12 2A10 10 0 0 0 2 12C2 16.42 4.87 20.17 8.84 21.5C9.34 21.58 9.5 21.27 9.5 21C9.5 20.77 9.5 20.14 9.5 19.31C6.73 19.91 6.14 17.97 6.14 17.97C5.68 16.81 5.03 16.5 5.03 16.5C4.12 15.88 5.1 15.9 5.1 15.9C6.1 15.97 6.63 16.93 6.63 16.93C7.5 18.45 8.97 18 9.54 17.76C9.63 17.11 9.89 16.67 10.17 16.42C7.95 16.17 5.62 15.31 5.62 11.5C5.62 10.39 6 9.5 6.65 8.79C6.55 8.54 6.2 7.5 6.75 6.15C6.75 6.15 7.59 5.88 9.5 7.17C10.29 6.95 11.15 6.84 12 6.84C12.85 6.84 13.71 6.95 14.5 7.17C16.41 5.88 17.25 6.15 17.25 6.15C17.8 7.5 17.45 8.54 17.35 8.79C18 9.5 18.38 10.39 18.38 11.5C18.38 15.32 16.04 16.16 13.81 16.41C14.17 16.72 14.5 17.33 14.5 18.26C14.5 19.6 14.5 20.68 14.5 21C14.5 21.27 14.66 21.59 15.17 21.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2Z"/>
              </svg>
            </div>
            <div className="flex-grow">
              <input
                type="text"
                placeholder="GitHub profile URL"
                defaultValue="https://github.com/johndoe"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button className="btn-primary py-2 px-6">
          Save Changes
        </button>
      </div>
    </div>
  );
};

const SettingsTab: React.FC = () => {
  return (
    <div>
      <h2 className="text-2xl font-medium mb-6">Account Settings</h2>

      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-medium mb-4">Password</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium mb-2">
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium mb-2">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div>
              <button className="btn-primary py-2 px-6">
                Update Password
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <h3 className="text-xl font-medium mb-4">Preferences</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Receive email notifications for account activity
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Two-Factor Authentication</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Add an extra layer of security to your account
                </p>
              </div>
              <button className="text-[#7F56D9] hover:underline">
                Enable
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Public Profile</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Make your profile visible to other users
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <h3 className="text-xl font-medium mb-4 text-red-400">Danger Zone</h3>

          <div className="bg-red-500/10 border border-red-500/20 rounded-md p-4">
            <p className="font-medium mb-2">Delete Account</p>
            <p className="text-sm text-[var(--color-muted)] mb-4">
              Once you delete your account, there is no going back. All your data will be permanently removed.
            </p>
            <button className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md transition-colors">
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentMethodsTab: React.FC = () => {
  return (
    <div>
      <h2 className="text-2xl font-medium mb-6">Payment Methods</h2>

      <div className="space-y-6">
        <div className="bg-[#2A2633] rounded-lg p-4 border border-white/10">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-white flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-blue-500">
                  <path d="M20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4ZM20 18H4V12H20V18ZM20 8H4V6H20V8Z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium">Visa ending in 4242</p>
                <p className="text-sm text-[var(--color-muted)]">Expires 04/2025</p>
              </div>
            </div>
            <div className="space-x-2">
              <button className="text-[#7F56D9] hover:underline text-sm">Edit</button>
              <button className="text-red-400 hover:underline text-sm">Remove</button>
            </div>
          </div>
          <div className="flex items-center">
            <span className="text-xs py-1 px-2 rounded-full bg-green-500/20 text-green-400">
              Default
            </span>
          </div>
        </div>

        <div className="bg-[#2A2633] rounded-lg p-4 border border-white/10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-white flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-[#FFB302]">
                  <path d="M20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4ZM20 18H4V12H20V18ZM20 8H4V6H20V8Z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium">Mastercard ending in 5678</p>
                <p className="text-sm text-[var(--color-muted)]">Expires 07/2024</p>
              </div>
            </div>
            <div className="space-x-2">
              <button className="text-[#7F56D9] hover:underline text-sm">Edit</button>
              <button className="text-red-400 hover:underline text-sm">Remove</button>
            </div>
          </div>
        </div>

        <button className="flex items-center gap-2 text-[#7F56D9] hover:underline">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Add Payment Method
        </button>
      </div>

      <div className="mt-8 border-t border-white/10 pt-6">
        <h3 className="text-xl font-medium mb-4">Billing Address</h3>

        <div className="bg-[#2A2633] rounded-lg p-4 border border-white/10 mb-4">
          <p className="font-medium">John Doe</p>
          <p className="text-[var(--color-muted)]">123 Developer St</p>
          <p className="text-[var(--color-muted)]">San Francisco, CA 94107</p>
          <p className="text-[var(--color-muted)]">United States</p>

          <div className="mt-4 space-x-2">
            <button className="text-[#7F56D9] hover:underline text-sm">Edit</button>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-xl font-medium mb-4">Billing History</h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Invoice</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10">
                  <td className="px-4 py-3">Jun 12, 2023</td>
                  <td className="px-4 py-3">$49.99</td>
                  <td className="px-4 py-3">
                    <span className="text-xs py-1 px-2 rounded-full bg-green-500/20 text-green-400">
                      Paid
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button className="text-[#7F56D9] hover:underline text-sm">Download</button>
                  </td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="px-4 py-3">May 12, 2023</td>
                  <td className="px-4 py-3">$49.99</td>
                  <td className="px-4 py-3">
                    <span className="text-xs py-1 px-2 rounded-full bg-green-500/20 text-green-400">
                      Paid
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button className="text-[#7F56D9] hover:underline text-sm">Download</button>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Apr 12, 2023</td>
                  <td className="px-4 py-3">$49.99</td>
                  <td className="px-4 py-3">
                    <span className="text-xs py-1 px-2 rounded-full bg-green-500/20 text-green-400">
                      Paid
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button className="text-[#7F56D9] hover:underline text-sm">Download</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreditsTab: React.FC = () => {
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [pendingCreditBalance, setPendingCreditBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<{
    assetsCount: number,
    totalSpent: number,
    totalEarned: number,
    pendingEarnings: number
  }>({
    assetsCount: 0,
    totalSpent: 0,
    totalEarned: 0,
    pendingEarnings: 0
  });
  const [showRedeemForm, setShowRedeemForm] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Check if user is authenticated by trying to fetch basic data
        const balance = await getCreditBalance();
        if (balance !== null && balance !== undefined) {
          setIsAuthenticated(true);
          setCreditBalance(balance);

          // Only fetch the rest of the data if we're authenticated
          const [pendingBalance, transactionHistory, creditStats] = await Promise.all([
            getPendingCreditBalance(),
            getTransactionHistory(),
            getCreditStats(),
          ]);

          setPendingCreditBalance(pendingBalance);
          setTransactions(transactionHistory);
          setStats(creditStats);
        }
      } catch (error) {
        console.error('Error loading credit data:', error);
        // If we get an error, assume it's authentication related
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Don't set up interval refreshes if not authenticated
    let intervalId: NodeJS.Timeout | null = null;

    if (isAuthenticated) {
      // Set up interval to refresh the data only if authenticated
      intervalId = setInterval(async () => {
        try {
          const [balance, pendingBalance, transactionHistory, creditStats] = await Promise.all([
            getCreditBalance(),
            getPendingCreditBalance(),
            getTransactionHistory(),
            getCreditStats(),
          ]);

          setCreditBalance(balance);
          setPendingCreditBalance(pendingBalance);
          setTransactions(transactionHistory);
          setStats(creditStats);
        } catch (error) {
          console.error('Error refreshing credit data:', error);
        }
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7F56D9]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="bg-[#2A1E44] rounded-lg p-6 text-center">
        <h3 className="text-xl font-semibold mb-4">Authentication Required</h3>
        <p className="mb-4">You need to be logged in to view your credit balance and transactions.</p>
        <a href="/login" className="inline-block py-2 px-6 bg-[#7F56D9] hover:bg-[#6941C6] text-white rounded-md transition">
          Go to Login
        </a>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg p-6 border border-white/5">
          <h3 className="text-lg font-semibold mb-4">Credit Balance</h3>
          <div className="flex items-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#7F56D9] to-[#9D4EDD] flex items-center justify-center text-white text-3xl font-bold mr-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <p className="text-[#A3A3A3] text-sm">Available Credits</p>
              <p className="text-3xl font-bold">{typeof creditBalance === 'number' ? creditBalance.toFixed(0) : '0'}</p>
              <p className="text-[#A3A3A3] text-xs mt-1">≈ ₿ {typeof creditBalance === 'number' ? (creditBalance / BTC_PRICE).toFixed(8) : '0.00000000'} BTC</p>
              {pendingCreditBalance > 0 && (
                <p className="text-xs text-amber-400 mt-1">
                  +{pendingCreditBalance.toFixed(0)} Credits pending release
                </p>
              )}
            </div>
          </div>
          <div className="flex mt-4 gap-2">
            <button
              onClick={() => setShowRedeemForm(!showRedeemForm)}
              className="flex-1 py-2 px-4 bg-[#2A1E44]/70 hover:bg-[#2A1E44] text-white rounded-md transition flex items-center justify-center"
            >
              {showRedeemForm ? 'Cancel' : 'Redeem to BTC'}
            </button>
            <a
              href="/topup"
              className="flex-1 py-2 px-4 bg-[#7F56D9] hover:bg-[#6941C6] text-white rounded-md transition flex items-center justify-center"
            >
              Buy More Credits
            </a>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg p-6 border border-white/5">
          <h3 className="text-lg font-semibold mb-4">Credits Stats</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#2A1E44]/70 rounded-lg p-4">
              <p className="text-[#A3A3A3] text-sm">Assets Purchased</p>
              <p className="text-2xl font-bold">{stats.assetsCount}</p>
            </div>
            <div className="bg-[#2A1E44]/70 rounded-lg p-4">
              <p className="text-[#A3A3A3] text-sm">Total Spent</p>
              <p className="text-2xl font-bold">{stats.totalSpent} Credits</p>
            </div>
            <div className="bg-[#2A1E44]/70 rounded-lg p-4">
              <p className="text-[#A3A3A3] text-sm">Earnings</p>
              <p className="text-2xl font-bold">{stats.totalEarned} Credits</p>
              {stats.pendingEarnings > 0 && (
                <p className="text-xs text-amber-400">+{stats.pendingEarnings} pending</p>
              )}
            </div>
            <div className="bg-[#2A1E44]/70 rounded-lg p-4">
              <p className="text-[#A3A3A3] text-sm">Current Rate</p>
              <p className="text-2xl font-bold">₿ {(1 / BTC_PRICE).toFixed(8)}</p>
              <p className="text-[#A3A3A3] text-xs">per credit</p>
            </div>
          </div>
        </div>
      </div>

      {/* Show pending earnings panel if there are pending earnings */}
      {stats.pendingEarnings > 0 && <PendingEarningsPanel />}

      {showRedeemForm && <RedeemCreditsForm creditBalance={creditBalance} />}

      <div className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg overflow-hidden border border-white/5">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-semibold">Transaction History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead className="bg-[#2A1E44]/40">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3A3A3] uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {transactions.length > 0 ? (
                transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{transaction.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.type === 'Top Up'
                          ? 'bg-green-100/10 text-green-400'
                          : transaction.type === 'Withdrawal'
                            ? 'bg-orange-100/10 text-orange-400'
                            : transaction.type === 'Sale'
                              ? 'bg-purple-100/10 text-purple-400'
                              : 'bg-blue-100/10 text-blue-400'
                      }`}>
                        {transaction.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {transaction.itemName || '-'}
                      {transaction.releaseDate && transaction.status === 'Held' && (
                        <span className="text-xs text-amber-400 block">
                          Available on {transaction.releaseDate}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {transaction.type === 'Purchase' ? '-' : '+'}{Math.abs(transaction.amount)} Credits
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.status === 'Completed'
                          ? 'bg-green-100/10 text-green-400'
                          : transaction.status === 'Failed'
                            ? 'bg-red-100/10 text-red-400'
                            : transaction.status === 'Held'
                              ? 'bg-amber-100/10 text-amber-400'
                              : 'bg-yellow-100/10 text-yellow-400'
                      }`}>
                        {transaction.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-[#A3A3A3]">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Form for redeeming credits to Bitcoin
const RedeemCreditsForm: React.FC<{creditBalance: number}> = ({ creditBalance }) => {
  const [amount, setAmount] = useState<string>('');
  const [btcAddress, setBtcAddress] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Check for pending credits
  useEffect(() => {
    const pendingBalance = getPendingCreditBalance();
    if (pendingBalance > 0) {
      setWarning(`Note: You have ${pendingBalance.toFixed(0)} credits pending release that cannot be withdrawn yet.`);
    } else {
      setWarning(null);
    }
  }, []);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBtcAddress(e.target.value);
  };

  const handleRedeem = async () => {
    // Validate input
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (amountNum > creditBalance) {
      setError('Insufficient credits');
      return;
    }

    if (!btcAddress || btcAddress.trim().length < 26) { // Basic BTC address validation
      setError('Please enter a valid Bitcoin address');
      return;
    }

    setError(null);
    setIsProcessing(true);

    try {
      // In a real app, this would make an API call to process the withdrawal
      await withdrawCredits(amountNum);

      // For demo purposes, just show a success message and redirect
      alert(`Success! ${amountNum} Credits will be converted to BTC and sent to ${btcAddress}`);
      setIsProcessing(false);
      setAmount('');
      setBtcAddress('');
    } catch (err) {
      setIsProcessing(false);
      const error = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(error);
    }
  };

  const getEstimatedBtc = (): string => {
    const amountNum = parseFloat(amount);
    if (!isNaN(amountNum) && amountNum > 0) {
      return (amountNum / BTC_PRICE).toFixed(8);
    }
    return '0.00000000';
  };

  return (
    <div className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg p-6 border border-white/5 mb-6">
      <h3 className="text-lg font-semibold mb-4">Redeem Credits to Bitcoin</h3>

      <div className="space-y-4">
        <div>
          <label htmlFor="redeemAmount" className="block text-sm font-medium mb-2">
            Amount to Redeem
          </label>
          <div className="relative">
            <input
              type="text"
              id="redeemAmount"
              value={amount}
              onChange={handleAmountChange}
              placeholder="Enter amount"
              disabled={isProcessing}
              className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-[#A3A3A3]">
              Credits
            </div>
          </div>
          <div className="mt-2 text-sm text-[#A3A3A3] flex justify-between">
            <span>Available: {creditBalance.toFixed(0)} Credits</span>
            <span>≈ ₿ {getEstimatedBtc()} BTC</span>
          </div>
        </div>

        <div>
          <label htmlFor="btcAddress" className="block text-sm font-medium mb-2">
            Bitcoin Address
          </label>
          <input
            type="text"
            id="btcAddress"
            value={btcAddress}
            onChange={handleAddressChange}
            placeholder="Enter your Bitcoin address"
            disabled={isProcessing}
            className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
          />
          <p className="mt-2 text-xs text-[#A3A3A3]">
            Make sure to enter a valid Bitcoin address. Transfers cannot be reversed.
          </p>
        </div>

        {warning && (
          <div className="bg-amber-400/10 rounded-lg p-4 text-sm text-amber-400">
            {warning}
          </div>
        )}

        {error && (
          <div className="bg-red-400/10 rounded-lg p-4 text-sm text-red-400">
            {error}
          </div>
        )}

        <button
          onClick={handleRedeem}
          disabled={isProcessing || !amount || !btcAddress}
          className={`w-full py-2 px-4 rounded-md transition ${
            isProcessing || !amount || !btcAddress
              ? 'bg-[#2A1E44]/70 text-white/50 cursor-not-allowed'
              : 'bg-[#7F56D9] hover:bg-[#6941C6] text-white'
          }`}
        >
          {isProcessing ? 'Processing...' : 'Redeem Credits'}
        </button>
      </div>
    </div>
  );
};

const NotificationsTab: React.FC = () => {
  return (
    <div>
      <h2 className="text-2xl font-medium mb-6">Notification Settings</h2>

      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-medium mb-4">Email Notifications</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Account Updates</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Emails about your account, security updates, and passwords
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">New Products & Updates</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Be the first to know about new products and platform updates
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Purchase Activity</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Receipts, payment confirmations, and delivery notifications
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Marketing Communications</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Promotions, discounts, and newsletters
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <h3 className="text-xl font-medium mb-4">Platform Notifications</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Direct Messages</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Notifications for messages sent directly to you
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Comment Replies</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Notifications when someone replies to your comment
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Order Updates</p>
                <p className="text-sm text-[var(--color-muted)]">
                  Notifications about your orders' status changes
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-[#2A2633] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7F56D9]"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <h3 className="text-xl font-medium mb-4">Notification Delivery</h3>

          <div className="space-y-6">
            <div>
              <p className="font-medium mb-3">Email Frequency</p>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="freq-instant"
                    type="radio"
                    name="frequency"
                    defaultChecked
                    className="w-4 h-4 text-[#7F56D9] bg-[#2A2633] border-white/10 focus:ring-[#7F56D9]"
                  />
                  <label htmlFor="freq-instant" className="ml-2 block">
                    Send emails as they happen
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="freq-daily"
                    type="radio"
                    name="frequency"
                    className="w-4 h-4 text-[#7F56D9] bg-[#2A2633] border-white/10 focus:ring-[#7F56D9]"
                  />
                  <label htmlFor="freq-daily" className="ml-2 block">
                    Daily digest
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="freq-weekly"
                    type="radio"
                    name="frequency"
                    className="w-4 h-4 text-[#7F56D9] bg-[#2A2633] border-white/10 focus:ring-[#7F56D9]"
                  />
                  <label htmlFor="freq-weekly" className="ml-2 block">
                    Weekly digest
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button className="btn-primary py-2 px-6">
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
